import { Link, usePage } from "@inertiajs/react"
import { Palette } from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar"
import { getVisibleAdminNavItems, getVisibleNavItems, getVisibleProjectNavItems, type NavItem } from "@/lib/navigation"
import type { SharedProps } from "@/types"

function isItemActive(url: string, item: NavItem): boolean {
  if (item.href === "/") return url === "/"
  // For query-param based hrefs (e.g. /projects?q[status_eq]=0)
  if (item.href.includes("?")) {
    const [path, query] = item.href.split("?")
    return url.startsWith(path) && url.includes(query)
  }
  return url === item.href || url.startsWith(item.href + "/")
}

export function AppSidebar() {
  const { url, props } = usePage<SharedProps>()
  const navItems = getVisibleNavItems(props.permissions)
  const projectItems = getVisibleProjectNavItems(props.permissions)
  const adminItems = getVisibleAdminNavItems(props.permissions)
  const user = props.current_user

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/" prefetch>
                <img src="/static/kaptcher-logo.png" alt="Kaptcher" className="size-8 shrink-0 object-contain" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Kaptcher</span>
                  {user?.organisation_name && (
                    <span className="text-muted-foreground truncate text-xs">{user.organisation_name}</span>
                  )}
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton asChild tooltip={item.title} isActive={isItemActive(url, item)}>
                  <Link href={item.href} prefetch>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        {projectItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Projets</SidebarGroupLabel>
            <SidebarMenu>
              {projectItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton asChild tooltip={item.title} isActive={isItemActive(url, item)}>
                    <Link href={item.href} prefetch>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
        {adminItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarMenu>
              {adminItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton asChild tooltip={item.title} isActive={isItemActive(url, item)}>
                    {item.external ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer">
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    ) : (
                      <Link href={item.href} prefetch>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        )}
        {import.meta.env.DEV && (
          <SidebarGroup>
            <SidebarGroupLabel>Développement</SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="UI Kit" isActive={url === "/ui-kit"}>
                  <a href="/ui-kit">
                    <Palette />
                    <span>UI Kit</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  )
}
